// src/pages/AddBlog.jsx
import React, { useContext, useState } from "react";
import { db, storage } from "./firebase/firebaseConfig";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { useNavigate } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import { UserContext } from "./context/UserContext";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";

// ===== Custom Upload Adapter for Firebase =====
class CustomUploadAdapter {
  constructor(loader) {
    this.loader = loader;
  }

  async upload() {
    return this.loader.file.then(
      (file) =>
        new Promise(async (resolve, reject) => {
          try {
            const storageRef = ref(
              storage,
              `editor-images/${Date.now()}-${file.name}`
            );
            await uploadBytes(storageRef, file);
            const downloadURL = await getDownloadURL(storageRef);

            resolve({
              default: downloadURL,
            });
          } catch (err) {
            reject(err);
          }
        })
    );
  }

  abort() {
    console.log("Image upload aborted");
  }
}

function MyCustomUploadAdapterPlugin(editor) {
  editor.plugins.get("FileRepository").createUploadAdapter = (loader) => {
    return new CustomUploadAdapter(loader);
  };
}

// ===== Styled Components =====
const Wrapper = styled.div`
  padding: 40px;
  max-width: 900px;
  margin: auto;
  color: #fff;
`;

const Heading = styled.h2`
  font-size: 2rem;
  font-weight: 700;
  color: #00d4ff;
  margin-bottom: 20px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  margin-bottom: 20px;
  border-radius: 8px;
  border: none;
  background: #1a1a1a;
  color: #fff;
`;

const Select = styled.select`
  width: 100%;
  padding: 12px;
  margin-bottom: 20px;
  border-radius: 8px;
  border: none;
  background: #1a1a1a;
  color: #fff;
`;

const ThumbnailPreview = styled.img`
  width: 200px;
  border-radius: 8px;
  margin-bottom: 15px;
  display: block;
`;

const EditorWrapper = styled.div`
  margin-bottom: 20px;
  background: #fff;
  border-radius: 8px;
  color: #000;
  padding: 10px;
`;

const Button = styled.button`
  padding: 12px 18px;
  border: none;
  border-radius: 8px;
  background: linear-gradient(135deg, #00d4ff, #00bcd4);
  color: #121212;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: linear-gradient(135deg, #00bcd4, #0097a7);
  }

  &:disabled {
    background: #555;
    cursor: not-allowed;
  }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const Loader = styled.div`
  border: 3px solid rgba(255, 255, 255, 0.2);
  border-top: 3px solid #121212;
  border-radius: 50%;
  width: 16px;
  height: 16px;
  animation: ${spin} 1s linear infinite;
  margin-left: 8px;
`;

// Categories
const technologyCategories = [
  "React",
  "React Native",
  "Angular",
  "Vue.js",
  "Next.js",
  "Node.js",
  "Express.js",
  "Django",
  "Flask",
  "Spring Boot",
  "Laravel",
  "Ruby on Rails",
  "Python",
  "Java",
  "JavaScript",
  "TypeScript",
  "C#",
  "C++",
  "Go",
  "PHP",
  "Kotlin",
  "Swift",
  "SQL",
  "MongoDB",
  "PostgreSQL",
  "MySQL",
  "Firebase",
  "Supabase",
  "AWS",
  "Google Cloud",
  "Azure",
  "DevOps",
  "Docker",
  "Kubernetes",
  "AI / Machine Learning",
  "Data Science",
  "Cybersecurity",
  "Blockchain",
  "Other",
];

const AddBlog = () => {
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("React");
  const [content, setContent] = useState("");
  const [thumbnail, setThumbnail] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { user } = useContext(UserContext);

  // Generate SEO-friendly slug
  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  };

  const handleSubmit = async () => {
    if (!title || !content || content === "<p><br></p>" || !thumbnail) {
      alert("⚠️ Title, content and thumbnail are required");
      return;
    }

    setLoading(true);

    try {
      const slug = generateSlug(title);

      // Upload thumbnail
      const storageRef = ref(storage, `blogs/${Date.now()}-${thumbnail.name}`);
      await uploadBytes(storageRef, thumbnail);
      const thumbnailURL = await getDownloadURL(storageRef);

      // Save blog
      await addDoc(collection(db, "blogs"), {
        title,
        slug,
        author: user?.name || "Admin",
        postedBy: user?.id || null,
        content,
        category,
        thumbnail: thumbnailURL,
        createdAt: serverTimestamp(),
      });

      alert("✅ Blog added successfully!");
      navigate("/admin-panel");
    } catch (err) {
      console.error("Error adding blog:", err);
      alert("❌ Error adding blog. Check console for details.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Wrapper>
      <Heading>Add New Blog</Heading>

      {/* Title */}
      <Input
        type="text"
        placeholder="Blog Title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
      />

      {/* Category */}
      <Select value={category} onChange={(e) => setCategory(e.target.value)}>
        {technologyCategories.map((tech) => (
          <option key={tech} value={tech}>
            {tech}
          </option>
        ))}
      </Select>

      {/* Thumbnail Upload + Preview */}
      {thumbnail && (
        <ThumbnailPreview src={URL.createObjectURL(thumbnail)} alt="Preview" />
      )}
      <Input
        type="file"
        accept="image/*"
        onChange={(e) => setThumbnail(e.target.files[0])}
      />

      {/* CKEditor with Image Upload */}
      <EditorWrapper>
        <CKEditor
          editor={ClassicEditor}
          data={content}
          onChange={(event, editor) => setContent(editor.getData())}
          config={{
            extraPlugins: [MyCustomUploadAdapterPlugin],
            toolbar: [
              "heading",
              "|",
              "bold",
              "italic",
              "link",
              "bulletedList",
              "numberedList",
              "blockQuote",
              "insertTable",
              "imageUpload", // ✅ Image upload button
              "undo",
              "redo",
            ],
          }}
        />
      </EditorWrapper>

      {/* Button */}
      <Button onClick={handleSubmit} disabled={loading}>
        {loading ? (
          <>
            Saving...
            <Loader />
          </>
        ) : (
          "Add Blog"
        )}
      </Button>
    </Wrapper>
  );
};

export default AddBlog;
